import { AlertRepository } from "../../repositories/AlertRrepository";
import { useTheme } from "@emotion/react";
import { useNavigate, useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { use, useEffect, useState } from "react";
import { NewGvmsDeclarationValidator } from "./NewGvmsDeclarationValidator";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import moment from "moment";
import "moment/locale/en-gb";
import { GvmsRepository } from "../../repositories/GvmsRepository";
import { OrganizationsRepository } from "../../repositories/OrganizationsRepository";
import { AuthRepository } from "../../repositories/AuthRepository";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ErrorMessageResolver } from "../../common/ErrorMessageResolver";
import {
  notifyShowErrorMessage,
  notifyShowSuccessMessage,
  triggerRerender,
} from "../../common/CommonActions";
import { useDispatch } from "react-redux";

const DECLARATION_TYPES = [
  { value: "CUSTOMS", label: "Customs Declaration" },
  { value: "TRANSIT", label: "Transit Declaration" },
  { value: "ATA_CARNET", label: "ATA Carnet" },
  { value: "TIR_CARNET", label: "TIR Carnet" },
  { value: "EIDR", label: "EIDR Declaration" },
  { value: "ORAL", label: "Oral or Declaration by Conduct" },
  { value: "MTP", label: "MTP Declaration" },
  { value: "EXEMPTION", label: "Exemption Declaration" },
];

const emptyForm = {
  correlationId: "",
  gmrId: "",
  emailDriver: "",
  transport: {
    vehicleRegNum: "",
    isUnaccompanied: false,
    trailerRegNum: [],
    containerRefNum: [],
    ssMrns: [],
    crossingID: "",
    // set default departure time to next day 00:00
    crossingDepartureDateTime: moment()
      .add(1, "day")
      .startOf("day")
      .toISOString(),
    departurePort: "",
    arrivalPort: "",
    carrierCompany: "",
    clientId: null,
    correlationId: "",
    organizationId: null,
  },
  contents: {
    emptyVehicle: null,
    customsDec: [],
    transitDec: [],
    tirDec: [],
    ataDec: [],
    dbcDec: null,
    eidrDec: [],
    mtpDec: null,
    exemptionDec: null,
  },
};

function FieldTitle({ children, hint }) {
  return (
    <Typography
      variant="caption"
      sx={{ fontWeight: 600, mb: 0.75, display: "block" }}
    >
      {children}
      {hint && (
        <Tooltip title={hint} arrow placement="top">
          <Icon
            fontSize="small"
            color="action"
            sx={{ ml: 0.5, verticalAlign: "middle" }}
          >
            help_outline
          </Icon>
        </Tooltip>
      )}
    </Typography>
  );
}

function SectionHeader({ children }) {
  return (
    <Typography variant="h6" gutterBottom>
      {children}
    </Typography>
  );
}

function DeclarationCard({ title, children, onDelete }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        <IconButton size="small" color="error" onClick={onDelete}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
      {children}
    </Paper>
  );
}

export default function GvmsDeclarationForm({ onClose, onSuccess, editingId, onDuplicate }) {
  const theme = useTheme();
  const { id: routeId } = useParams();
  const id = editingId ?? routeId ?? null;
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState(emptyForm);
  const [isEmptyVehicle, setIsEmptyVehicle] = useState(false);
  const [declarations, setDeclarations] = useState([]);
  const [selectedDecType, setSelectedDecType] = useState("");
  const [departureOptions, setDepartureOptions] = useState([]);
  const [arrivalOptions, setArrivalOptions] = useState([]);
  const [carrierOptions, setCarrierOptions] = useState([]);
  const [allRoutes, setAllRoutes] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await AlertRepository.getAllActiveByType({ type: "GVMS" });
        setAlerts(response.data);
      } catch (error) {
        console.error("Failed to fetch alerts: ", error);
      }
    };
    fetchAlerts();
  }, []);

  const hasErrorAlert = alerts?.some((alert) => alert?.severity === "error");

  const [direction, setDirection] = useState("");

  const dispatch = useDispatch();

  const [declaration, setDeclaration] = useState(null);
  const [currentUserOrganization, setCurrentUserOrganization] = useState(null);
  const [loadingDeclaration, setLoadingDeclaration] = useState(true);
  const [popup, setPopup] = useState({
    open: false,
    message: "",
    severity: "error",
  });

/** -------- Duplicate consignment -------- */

  const allowedStatusesDuplicate = ["ERROR", "DRAFT", "COMPLETED"];
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);

  const handleDuplicateDialogOpen = () => {
    setOpenDuplicateDialog(true);
  };

  const handleCloseDuplicateDialog = () => {
    setOpenDuplicateDialog(false);
  };

  const duplicateDeclaration = () => {
    if (!declaration?.id) return;
    setLoading(true);
    GvmsRepository.duplicate(declaration.id)
      .then((response) => {
        dispatch(notifyShowSuccessMessage("Declaration duplicated successfully"));

        const id = response.data?.id;
        if (!id) throw new Error("Duplicate consignment returned no ID");

        handleCloseDuplicateDialog();

        // Store the new ID so the new tab can pick it up
        sessionStorage.setItem("openDeclarationId", id);
        window.open("/gvms", "_blank");

        if (onDuplicate) onDuplicate();
      })
      .catch((err) => {
        dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!id) {
      const userDetails = AuthRepository.getUserDetails();
      setCurrentUserOrganization(userDetails?.organization ?? null);
      setFormData({
        ...emptyForm,
        organization: userDetails?.organization ?? null,
      });
      setLoadingDeclaration(false);
      return;
    }
    GvmsRepository.getById(id)
      .then((res) => {
        setDeclaration(res.data);
        setLoadingDeclaration(false);
      })
      .catch((err) => {
        console.error(err);
        setLoadingDeclaration(false);
      });
  }, [id]);

  useEffect(() => {
    GvmsRepository.getRoutes()
      .then((res) => {
        setAllRoutes(res.data);
        const unique = [
          ...new Set(res.data.map((r) => r.departurePort)),
        ].sort();
        setDepartureOptions(unique);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    const userDetails = AuthRepository.getUserDetails();
    setCurrentUserOrganization(userDetails?.organization ?? null);
  }, []);

  useEffect(() => {
    if (!declaration) {
      setFormData({
        ...emptyForm,
        organization: currentUserOrganization ?? null,
      });
      setIsEmptyVehicle(false);
      setDeclarations([]);
      return;
    }

    const normalized = {
      ...emptyForm,
      ...declaration,
      transport: { ...emptyForm.transport, ...declaration.transport },
      contents: { ...emptyForm.contents, ...declaration.contents },
      organization: declaration.organization || null,
    };
    console.log("Normalized declaration:", normalized);
    setFormData(normalized);

    const hasEmptyVehicle = Boolean(
      normalized.contents.emptyVehicle !== null &&
      normalized.contents.emptyVehicle !== undefined,
    );
    setIsEmptyVehicle(hasEmptyVehicle);

    const rebuilt = [];

    normalized.contents.customsDec?.forEach((d) =>
      rebuilt.push({
        type: "CUSTOMS",
        customsDecId: d.customsDecId ?? d.declarationId ?? d.id ?? "",
        ssRefNum: d.ssRefNum ?? "",
      }),
    );

    normalized.contents.transitDec?.forEach((d) =>
      rebuilt.push({
        type: "TRANSIT",
        mrn: d.mrn ?? "",
        ssRefNum: d.ssRefNum ?? "",
        isTSAD: d.isTSAD ?? false,
      }),
    );

    normalized.contents.tirDec?.forEach((d) =>
      rebuilt.push({
        type: "TIR_CARNET",
        carnetId: d.carnetId ?? "",
        ssRefNum: d.ssRefNum ?? "",
      }),
    );

    normalized.contents.eidrDec?.forEach((d) =>
      rebuilt.push({
        type: "EIDR",
        tin: d.tin ?? "",
        ssRefNum: d.ssRefNum ?? "",
      }),
    );

    normalized.contents.ataDec?.forEach((d) =>
      rebuilt.push({
        type: "ATA_CARNET",
        carnetId: d.carnetId ?? "",
        ssRefNum: d.ssRefNum ?? "",
      }),
    );
    if (normalized.contents.dbcDec) {
      rebuilt.push({
        type: "ORAL",
        isOwnVehicle: normalized.contents.dbcDec.isOwned ?? false,
        ssRefNum: normalized.contents.dbcDec.ssRefNum ?? "",
      });
    }
    if (normalized.contents.mtpDec) {
      rebuilt.push({
        type: "MTP",
        ssRefNum: normalized.contents.mtpDec.ssRefNum ?? [],
      });
    }
    if (normalized.contents.exemptionDec?.ssRefNum) {
      normalized.contents.exemptionDec.ssRefNum.forEach((ref) => {
        rebuilt.push({
          type: "EXEMPTION",
          ssRefNum: ref,
        });
      });
    }

    setDeclarations(rebuilt);

    console.log(formData.id);

    if (normalized.transport.departurePort && allRoutes.length > 0) {
      const arrivals = [
        ...new Set(
          allRoutes
            .filter(
              (r) => r.departurePort === normalized.transport.departurePort,
            )
            .map((r) => r.destinationPort),
        ),
      ];
      setArrivalOptions(arrivals);

      if (normalized.transport.arrivalPort) {
        const carriers = [
          ...new Set(
            allRoutes
              .filter(
                (r) =>
                  r.departurePort === normalized.transport.departurePort &&
                  r.destinationPort === normalized.transport.arrivalPort,
              )
              .map((r) => r.carrier),
          ),
        ];
        setCarrierOptions(carriers);
      }
    }
  }, [declaration, allRoutes, currentUserOrganization]);

  if (loadingDeclaration) return null;

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
    },
  };

  const handleClose = () => {
    setFormData({
      ...emptyForm,
      organization: currentUserOrganization ?? null,
    });
    setIsEmptyVehicle(false);
    setDeclarations([]);
    setSelectedDecType("");
    setErrors({});
    if (onClose) onClose();
  };

  const handleTransportChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      transport: { ...prev.transport, [field]: value },
    }));
  };

  const handleArrayAdd = (field) => {
    setFormData((prev) => ({
      ...prev,
      transport: { ...prev.transport, [field]: [...prev.transport[field], ""] },
    }));
  };

  const handleArrayChange = (field, index, value) => {
    const updated = [...formData.transport[field]];
    updated[index] = value;
    setFormData((prev) => ({
      ...prev,
      transport: { ...prev.transport, [field]: updated },
    }));
  };

  const handleArrayRemove = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      transport: {
        ...prev.transport,
        [field]: prev.transport[field].filter((_, i) => i !== index),
      },
    }));
  };

  const handleEmptyVehicleToggle = (checked) => {
    setIsEmptyVehicle(checked);
    setFormData((prev) => ({
      ...prev,
      contents: {
        ...prev.contents,
        emptyVehicle: checked ? { isOwned: false, ssRefNum: "" } : null,
      },
    }));
    if (checked) setDeclarations([]);
  };

  const handleEmptyVehicleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      contents: {
        ...prev.contents,
        emptyVehicle: { ...prev.contents.emptyVehicle, [field]: value },
      },
    }));
  };

  const handleDepartureSelect = (value) => {
    handleTransportChange("departurePort", value || "");
    handleTransportChange("arrivalPort", "");
    handleTransportChange("carrierCompany", "");
    handleTransportChange("crossingID", "");
    setArrivalOptions([]);
    setCarrierOptions([]);

    if (value) {
      const unique = [
        ...new Set(
          allRoutes
            .filter((r) => r.departurePort === value)
            .map((r) => r.destinationPort),
        ),
      ];
      setArrivalOptions(unique);
    }
  };

  const handleArrivalSelect = (value) => {
    handleTransportChange("arrivalPort", value || "");
    handleTransportChange("carrierCompany", "");
    handleTransportChange("crossingID", "");
    setCarrierOptions([]);

    if (value) {
      const matching = allRoutes.filter(
        (r) =>
          r.departurePort === formData.transport.departurePort &&
          r.destinationPort === value,
      );
      const unique = [...new Set(matching.map((r) => r.carrier))];
      setCarrierOptions(unique);
    }
  };

  const handleCarrierSelect = (value) => {
    handleTransportChange("carrierCompany", value || "");
    const route = allRoutes.find(
      (r) =>
        r.departurePort === formData.transport.departurePort &&
        r.destinationPort === formData.transport.arrivalPort &&
        r.carrier === value,
    );
    if (route) {
      handleTransportChange("crossingID", route.crossingID);
      setDirection(route.direction);
    }
  };

  const handleAddDeclaration = () => {
    if (!selectedDecType) return;

    const newDec = { type: selectedDecType };

    if (selectedDecType === "CUSTOMS") {
      newDec.customsDecId = "";
      newDec.ssRefNum = "";
    }
    if (selectedDecType === "TRANSIT") {
      newDec.mrn = "";
      newDec.ssRefNum = "";
      newDec.isTSAD = false;
    }
    if (selectedDecType === "ATA_CARNET" || selectedDecType === "TIR_CARNET") {
      newDec.carnetId = "";
      newDec.ssRefNum = "";
    }
    if (selectedDecType === "EIDR") {
      newDec.tin = "";
      newDec.ssRefNum = "";
    }
    if (selectedDecType === "ORAL") {
      newDec.isOwnVehicle = false;
      newDec.ssRefNum = "";
    }
    if (selectedDecType === "EXEMPTION") {
      newDec.ssRefNum = "";
    }
    if (selectedDecType === "MTP") {
      newDec.ssRefNum = [];
    }

    setDeclarations((prev) => [...prev, newDec]);
    setSelectedDecType("");
  };

  const handleDeclarationChange = (index, field, value) => {
    const updated = [...declarations];
    updated[index] = { ...updated[index], [field]: value };
    setDeclarations(updated);
  };

  const handleDeclarationRemove = (index) => {
    setDeclarations((prev) => prev.filter((_, i) => i !== index));
  };

  const buildContents = (data) => {
    const contents = {};

    contents.customsDec = declarations
      .filter((d) => d.type === "CUSTOMS")
      .map(({ customsDecId, ssRefNum }) => ({
        id: customsDecId || "",
        ssRefNum: ssRefNum || "",
      }));

    contents.transitDec = declarations
      .filter((d) => d.type === "TRANSIT")
      .map(({ mrn, ssRefNum, isTSAD }) => ({
        mrn: mrn?.trim() || "",
        ssRefNum: ssRefNum || null,
        isTSAD: isTSAD ?? false,
      }));

    contents.tirDec = declarations
      .filter((d) => d.type === "TIR_CARNET")
      .map(({ carnetId, ssRefNum }) => ({
        carnetId: carnetId || "",
        ssRefNum: ssRefNum || "",
      }));

    contents.eidrDec = declarations
      .filter((d) => d.type === "EIDR")
      .map(({ tin, ssRefNum }) => ({
        tin: tin || "",
        ssRefNum: ssRefNum || "",
      }));

    contents.ataDec = declarations
      .filter((d) => d.type === "ATA_CARNET")
      .map(({ carnetId, ssRefNum }) => ({
        carnetId: carnetId || "",
        ssRefNum: ssRefNum || null,
      }));

    const dbc = declarations.find((d) => d.type === "ORAL");
    contents.dbcDec = dbc
      ? {
          isOwned: dbc.isOwnVehicle || false,
          ssRefNum: Array.isArray(dbc.ssRefNum)
            ? (dbc.ssRefNum[0] ?? "")
            : dbc.ssRefNum || "",
        }
      : null;

    const mtpList = declarations.filter((d) => d.type === "MTP");
    const mtpRefs = mtpList.flatMap((d) => d.ssRefNum || []).filter(Boolean);
    contents.mtpDec = mtpRefs.length > 0 ? { ssRefNum: mtpRefs } : null;

    const exemptionList = declarations.filter((d) => d.type === "EXEMPTION");
    const exemptionRefs = exemptionList.map((d) => d.ssRefNum).filter(Boolean);
    contents.exemptionDec =
      exemptionRefs.length > 0 ? { ssRefNum: exemptionRefs } : null;

    contents.emptyVehicle = data.contents.emptyVehicle
      ? {
          isOwned: data.contents.emptyVehicle.isOwned || false,
          ssRefNum: data.contents.emptyVehicle.ssRefNum || "",
        }
      : null;

    return contents;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const payload = {
        organization: formData.organization,
        transport: {
          ...formData.transport,
          trailerRegNum: (formData.transport.trailerRegNum || []).filter(
            Boolean,
          ),
          containerRefNum: (formData.transport.containerRefNum || []).filter(
            Boolean,
          ),
          ssMrns: (formData.transport.ssMrns || []).filter(Boolean),
        },
        contents: {
          ...buildContents(formData),
        },
      };
      console.log(payload);
      // return;

      await NewGvmsDeclarationValidator.validate(payload, {
        abortEarly: false,
      });

      const isEditing = Boolean(declaration?.id);

      const response = isEditing
        ? await GvmsRepository.updateDeclaration({
            ...declaration,
            transport: payload.transport,
            contents: payload.contents,
            organization: formData.organization,
          })
        : await GvmsRepository.createDeclaration(payload);

      setPopup({
        open: true,
        message: "Saved successfully!",
        severity: "success",
      });

      setPopup({
        open: true,
        message: "Saved successfully!",
        severity: "success",
      });

      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 800);
    } catch (err) {
      if (err.name === "ValidationError") {
        const errorMap = {};
        err.inner?.forEach((e) => {
          errorMap[e.path] = e.message;
        });
        setErrors(errorMap);
        setPopup({
          open: true,
          message: err.inner?.[0]?.message || "Please fix validation errors",
          severity: "error",
        });
      } else if (err.response) {
        setPopup({
          open: true,
          message:
            typeof err.response.data === "string"
              ? err.response.data
              : err.response.data?.message || "Server error",
          severity: "error",
        });
      } else {
        setPopup({
          open: true,
          message: "Something went wrong",
          severity: "error",
        });
      }
      setLoading(false);
    }
  };

  const handleDraft = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const payload = {
        organization: formData.organization,
        transport: {
          ...formData.transport,
          trailerRegNum: (formData.transport.trailerRegNum || []).filter(
            Boolean,
          ),
          containerRefNum: (formData.transport.containerRefNum || []).filter(
            Boolean,
          ),
          ssMrns: (formData.transport.ssMrns || []).filter(Boolean),
        },
        contents: {
          ...buildContents(formData),
        },
      };
      console.log(payload);

      const isEditing = Boolean(declaration?.id);

      const response = isEditing
        ? await GvmsRepository.updateDraftDeclaration({
            ...declaration,
            transport: payload.transport,
            contents: payload.contents,
            organization: formData.organization,
          })
        : await GvmsRepository.createDraftDeclaration(
            payload,
            formData.organization?.id,
          );

      if (onSuccess) onSuccess(response.data);

      setPopup({
        open: true,
        message: "Saved successfully!",
        severity: "success",
      });
    } catch (err) {
      if (err.name === "ValidationError") {
        const errorMap = {};
        err.inner?.forEach((e) => {
          errorMap[e.path] = e.message;
        });
        setErrors(errorMap);
        setPopup({
          open: true,
          message: err.inner?.[0]?.message || "Please fix validation errors",
          severity: "error",
        });
      } else if (err.response) {
        setPopup({
          open: true,
          message:
            typeof err.response.data === "string"
              ? err.response.data
              : err.response.data?.message || "Server error",
          severity: "error",
        });
      } else {
        setPopup({
          open: true,
          message: "Something went wrong",
          severity: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const reject = () => {
    setLoading(true);
    GvmsRepository.setRejected(declaration?.id)
      .then((response) => {
        if (onSuccess) onSuccess(response.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
        setLoading(false);
      });
  };

  return (
    <>
      <Dialog open maxWidth="lg" fullScreen={isMobile} fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", fontSize: "20px" }}>
            <Icon color="primary" fontSize="large">
              assignment
            </Icon>

            <span
              style={{ marginLeft: 8, color: "#323232", lineHeight: "1.2em" }}
            >
              {declaration?.id ? "GVMS Declaration" : "New GVMS Declaration"}
            </span>

            {declaration?.gmrState && (
              <Chip
                label={declaration.gmrState}
                size="small"
                sx={{ ml: 1 }}
                color={
                  declaration.gmrState === "ERROR" ||
                  declaration.gmrState === "NOT_FINALISABLE"
                    ? "error"
                    : declaration.gmrState === "COMPLETED" ||
                        declaration.gmrState === "FINALISED"
                      ? "success"
                      : "primary"
                }
              />
            )}

            {formData?.gmrId && (
              <Chip
                label={`GMR: ${formData.gmrId}`}
                color="info"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            )}
          </Box>

          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ overflowY: "auto", px: { xs: 1.5, md: 3 } }}>
          {alerts.filter((alert) => alert.severity === "error").map((alert) => (
            <Alert severity={alert.severity} key={alert.id} sx={{ mb: 2 }}>
              {alert.description}
            </Alert>
          ))}
          <Grid container spacing={{ xs: 1, md: 2 }}>
            <Grid item xs={12}>
              {declaration?.declarationIssues?.length > 0 &&
                declaration.declarationIssues.map((issue, index) => (
                  <Box key={`${issue?.code}-${index}`} mb={1}>
                    {index === 0 && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        <AlertTitle>Note</AlertTitle>
                        Please check and correct the following issues before
                        saving the declaration.
                      </Alert>
                    )}
                    <Alert severity="error">
                      <AlertTitle>{issue?.code || "Error"}</AlertTitle>
                      {issue?.humanizedText ?? issue?.description}
                      {issue?.field && (
                        <>
                          <br />
                          <strong>Field:</strong> {issue.field}
                        </>
                      )}
                      {issue?.value && (
                        <>
                          <br />
                          <strong>Value:</strong> {issue.value}
                        </>
                      )}
                    </Alert>
                  </Box>
                ))}
            </Grid>

            <Grid item xs={12}>
              <SectionHeader>General Details</SectionHeader>
            </Grid>

            <Grid item xs={12} md={4}>
              <Autocomplete
                options={clientOptions}
                getOptionLabel={(opt) => opt?.nameEn ?? ""}
                value={formData.organization ?? null}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                disabled
                onChange={(_, value) =>
                  setFormData((p) => ({
                    ...p,
                    organization: value ?? null,
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client"
                    fullWidth
                    size="small"
                    sx={inputSx}
                  />
                )}
                noOptionsText="No clients found"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Driver Email"
                value={formData.emailDriver}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, emailDriver: e.target.value }))
                }
                fullWidth
                size="small"
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="GMR ID"
                value={formData.gmrId || ""}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, gmrId: e.target.value }))
                }
                fullWidth
                size="small"
                sx={inputSx}
                disabled
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <SectionHeader>Vehicle Details</SectionHeader>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Vehicle Reg Num"
                value={formData.transport.vehicleRegNum}
                onChange={(e) =>
                  handleTransportChange(
                    "vehicleRegNum",
                    e.target.value
                      .trim()
                      .replace(/[^a-z0-9]/gi, "")
                      .toUpperCase(),
                  )
                }
                fullWidth
                size="small"
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ pt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!formData.transport.isUnaccompanied}
                      onChange={(e) =>
                        handleTransportChange(
                          "isUnaccompanied",
                          !e.target.checked,
                        )
                      }
                    />
                  }
                  label="Accompanied"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isEmptyVehicle}
                      onChange={(e) =>
                        handleEmptyVehicleToggle(e.target.checked)
                      }
                    />
                  }
                  label="Empty Vehicle"
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <FieldTitle hint="Safety and Security MRNs">
                  S&S MRNs
                </FieldTitle>

                <Box display="flex" flexWrap="wrap" gap={1}>
                  {(formData.transport.ssMrns || []).map((val, i) => (
                    <Box key={i} display="flex" gap={1} alignItems="center">
                      <TextField
                        value={val}
                        onChange={(e) =>
                          handleArrayChange("ssMrns", i, e.target.value)
                        }
                        size="small"
                        sx={{ ...inputSx, minWidth: 220 }}
                        placeholder={`S&S MRN ${i + 1}`}
                        error={!!errors[`transport.ssMrns.${i}`]}
                        helperText={errors[`transport.ssMrns.${i}`]}
                      />
                      <IconButton
                        onClick={() => handleArrayRemove("ssMrns", i)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}

                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleArrayAdd("ssMrns")}
                  >
                    Add S&S MRN
                  </Button>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <FieldTitle hint="Can be multiple">Trailer Reg Nums</FieldTitle>
                {(formData.transport.trailerRegNum || []).map((val, i) => (
                  <Box key={i} display="flex" gap={1} mb={1}>
                    <TextField
                      value={val}
                      onChange={(e) =>
                        handleArrayChange(
                          "trailerRegNum",
                          i,
                          e.target.value
                            .trim()
                            .replace(/[^a-z0-9]/gi, "")
                            .toUpperCase(),
                        )
                      }
                      size="small"
                      fullWidth
                      placeholder={`Trailer ${i + 1}`}
                    />
                    <IconButton
                      onClick={() => handleArrayRemove("trailerRegNum", i)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleArrayAdd("trailerRegNum")}
                >
                  Add Trailer
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <FieldTitle hint="If goods are in container">
                  Container Ref Nums
                </FieldTitle>
                {(formData.transport.containerRefNum || []).map((val, i) => (
                  <Box key={i} display="flex" gap={1} mb={1}>
                    <TextField
                      value={val}
                      onChange={(e) =>
                        handleArrayChange("containerRefNum", i, e.target.value)
                      }
                      size="small"
                      fullWidth
                      placeholder={`Container ${i + 1}`}
                    />
                    <IconButton
                      onClick={() => handleArrayRemove("containerRefNum", i)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleArrayAdd("containerRefNum")}
                >
                  Add Container
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <SectionHeader>Crossing Details</SectionHeader>
            </Grid>

            <Grid item xs={12} md={3}>
              <Autocomplete
                options={departureOptions}
                value={formData.transport.departurePort || null}
                onChange={(_, value) => handleDepartureSelect(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Departure Port"
                    fullWidth
                    size="small"
                    sx={inputSx}
                  />
                )}
                noOptionsText="No ports found"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Autocomplete
                options={arrivalOptions}
                value={formData.transport.arrivalPort || null}
                onChange={(_, value) => handleArrivalSelect(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Arrival Port"
                    fullWidth
                    size="small"
                    sx={inputSx}
                  />
                )}
                noOptionsText="No ports found"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Autocomplete
                options={carrierOptions}
                value={formData.transport.carrierCompany || null}
                onChange={(_, value) => handleCarrierSelect(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Carrier"
                    fullWidth
                    size="small"
                    sx={inputSx}
                  />
                )}
                noOptionsText="No carriers found"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale="en-gb"
              >
                <DateTimePicker
                  renderInput={(props) => <TextField {...props} size="small" />}
                  label="Crossing Date & Time"
                  size="small"
                  value={
                    formData.transport.crossingDepartureDateTime
                      ? moment(formData.transport.crossingDepartureDateTime)
                      : null
                  }
                  onChange={(newValue) => {
                    handleTransportChange(
                      "crossingDepartureDateTime",
                      newValue,
                    );
                  }}
                  format="DD.MM.YYYY HH:mm"
                  minDate={moment()}
                  slotProps={{
                    textField: { size: "small", fullWidth: true },
                    actionBar: { actions: ["clear", "accept"] },
                  }}
                  ampm={false}
                />
              </LocalizationProvider>
            </Grid>

            {isEmptyVehicle && (
              <>
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderColor: theme.palette.primary.main,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ color: theme.palette.primary.main, mb: 2 }}
                    >
                      Empty Vehicle Details
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Empty Vehicle S&S MRN"
                          value={formData.contents.emptyVehicle?.ssRefNum || ""}
                          onChange={(e) =>
                            handleEmptyVehicleChange("ssRefNum", e.target.value)
                          }
                          fullWidth
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                formData.contents.emptyVehicle?.isOwned || false
                              }
                              onChange={(e) =>
                                handleEmptyVehicleChange(
                                  "isOwned",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label="Owned vehicle"
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </>
            )}

            {!isEmptyVehicle && (
              <>
                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={2}
                    flexWrap="wrap"
                  >
                    <SectionHeader>Declarations</SectionHeader>

                    <FormControl size="small" sx={{ minWidth: 260 }}>
                      <InputLabel>Declaration Type</InputLabel>
                      <Select
                        label="Declaration Type"
                        value={selectedDecType}
                        onChange={(e) => setSelectedDecType(e.target.value)}
                      >
                        <MenuItem value="">
                          <em>Select declaration type</em>
                        </MenuItem>
                        {DECLARATION_TYPES.filter(
                          (t) => t.value !== "EIDR" || direction === "GB",
                        ).map((t) => (
                          <MenuItem key={t.value} value={t.value}>
                            {t.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddDeclaration}
                    >
                      Add
                    </Button>
                  </Box>
                </Grid>

                {declarations.length === 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      No declarations added yet.
                    </Typography>
                  </Grid>
                )}

                {declarations.map((dec, i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <DeclarationCard
                      title={
                        DECLARATION_TYPES.find((t) => t.value === dec.type)
                          ?.label || dec.type
                      }
                      onDelete={() => handleDeclarationRemove(i)}
                    >
                      <Grid container spacing={2}>
                        {dec.type === "CUSTOMS" && (
                          <>
                            <Grid item xs={12} md={6}>
                              <TextField
                                label="Customs Declaration ID"
                                value={dec.customsDecId || ""}
                                onChange={(e) =>
                                  handleDeclarationChange(
                                    i,
                                    "customsDecId",
                                    e.target.value,
                                  )
                                }
                                fullWidth
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                label="S&S Ref Number"
                                value={dec.ssRefNum || ""}
                                onChange={(e) =>
                                  handleDeclarationChange(
                                    i,
                                    "ssRefNum",
                                    e.target.value,
                                  )
                                }
                                fullWidth
                                size="small"
                              />
                            </Grid>
                          </>
                        )}

                        {dec.type === "TRANSIT" && (
                          <>
                            <Grid item xs={12} md={6}>
                              <TextField
                                label="Transit Declaration ID"
                                value={dec.mrn || ""}
                                onChange={(e) =>
                                  handleDeclarationChange(
                                    i,
                                    "mrn",
                                    e.target.value,
                                  )
                                }
                                fullWidth
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                label="S&S Ref Number"
                                value={dec.ssRefNum || ""}
                                onChange={(e) =>
                                  handleDeclarationChange(
                                    i,
                                    "ssRefNum",
                                    e.target.value,
                                  )
                                }
                                fullWidth
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={dec.isTSAD || false}
                                    onChange={(e) =>
                                      handleDeclarationChange(
                                        i,
                                        "isTSAD",
                                        e.target.checked,
                                      )
                                    }
                                  />
                                }
                                label="TSAD"
                              />
                            </Grid>
                          </>
                        )}

                        {(dec.type === "ATA_CARNET" ||
                          dec.type === "TIR_CARNET") && (
                          <>
                            <Grid item xs={12} md={6}>
                              <TextField
                                label="Carnet ID"
                                value={dec.carnetId || ""}
                                onChange={(e) =>
                                  handleDeclarationChange(
                                    i,
                                    "carnetId",
                                    e.target.value,
                                  )
                                }
                                fullWidth
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                label="S&S Ref Number"
                                value={dec.ssRefNum || ""}
                                onChange={(e) =>
                                  handleDeclarationChange(
                                    i,
                                    "ssRefNum",
                                    e.target.value,
                                  )
                                }
                                fullWidth
                                size="small"
                              />
                            </Grid>
                          </>
                        )}

                        {dec.type === "EIDR" && (
                          <>
                            <Grid item xs={12} md={6}>
                              <TextField
                                label="TIN"
                                value={dec.tin || ""}
                                onChange={(e) =>
                                  handleDeclarationChange(
                                    i,
                                    "tin",
                                    e.target.value,
                                  )
                                }
                                fullWidth
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                label="S&S Ref Number"
                                value={dec.ssRefNum || ""}
                                onChange={(e) =>
                                  handleDeclarationChange(
                                    i,
                                    "ssRefNum",
                                    e.target.value,
                                  )
                                }
                                fullWidth
                                size="small"
                              />
                            </Grid>
                          </>
                        )}

                        {dec.type === "ORAL" && (
                          <>
                            <Grid item xs={12} md={6}>
                              <TextField
                                label="S&S Ref Number"
                                value={dec.ssRefNum || ""}
                                onChange={(e) =>
                                  handleDeclarationChange(
                                    i,
                                    "ssRefNum",
                                    e.target.value,
                                  )
                                }
                                fullWidth
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={dec.isOwnVehicle || false}
                                    onChange={(e) =>
                                      handleDeclarationChange(
                                        i,
                                        "isOwnVehicle",
                                        e.target.checked,
                                      )
                                    }
                                  />
                                }
                                label="Own vehicle"
                              />
                            </Grid>
                          </>
                        )}

                        {dec.type === "EXEMPTION" && (
                          <Grid item xs={12}>
                            <TextField
                              label="S&S Ref Number"
                              value={dec.ssRefNum || ""}
                              onChange={(e) =>
                                handleDeclarationChange(
                                  i,
                                  "ssRefNum",
                                  e.target.value,
                                )
                              }
                              fullWidth
                              size="small"
                            />
                          </Grid>
                        )}
                        {dec.type === "MTP" && (
                          <Grid item xs={12}>
                            <FieldTitle hint="Safety and Security MRNs for MTP">
                              S&S Ref Numbers
                            </FieldTitle>

                            {(dec.ssRefNum || []).map((ss, j) => (
                              <TextField
                                key={j}
                                value={ss}
                                onChange={(e) =>
                                  handleDeclarationChange(
                                    i,
                                    "ssRefNum",
                                    (dec.ssRefNum || []).map((v, idx) =>
                                      idx === j ? e.target.value : v,
                                    ),
                                  )
                                }
                                size="small"
                                fullWidth
                                sx={{ ...inputSx, mb: 1 }}
                                placeholder={`MTP S&S Ref Num ${j + 1}`}
                                error={
                                  !!errors[`declarations.${i}.ssRefNum.${j}`]
                                }
                                helperText={
                                  errors[`declarations.${i}.ssRefNum.${j}`]
                                }
                              />
                            ))}

                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() =>
                                handleDeclarationChange(i, "ssRefNum", [
                                  ...(dec.ssRefNum || []),
                                  "",
                                ])
                              }
                              sx={{ mt: 0.5 }}
                            >
                              Add S&S Ref Num
                            </Button>
                          </Grid>
                        )}
                      </Grid>
                    </DeclarationCard>
                  </Grid>
                ))}
              </>
            )}
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            flexDirection: { xs: "column-reverse", md: "row" },
            gap: 1,
            p: { xs: 2, md: 1 },
          }}
        >
          <Button
            onClick={handleSubmit}
            color="success"
            startIcon={<SaveIcon />}
            disabled={
              loading ||
              hasErrorAlert ||
              [
                "FINALISED",
                "CHECKED_IN",
                "EMBARKED",
                "COMPLETED",
                "CREATED",
                "SENT",
                "APPROVED",
              ].includes(declaration?.gmrState)
            }
          >
            Save
          </Button>
          {(!declaration || declaration?.gmrState === "DRAFT") && (
            <Button
              onClick={handleDraft}
              color="error"
              startIcon={<Icon>save</Icon>}
              disabled={loading}
            >
              Draft
            </Button>
          )}
          <Button
            onClick={handleDuplicateDialogOpen}
            color="info"
            startIcon={<Icon>file_copy</Icon>}
            style={{ marginRight: "20px" }}
            disabled={
              loading ||
              !declaration ||
              !allowedStatusesDuplicate.includes(declaration.gmrState)
            }
          >
            Duplicate
          </Button>
          <Button
            onClick={reject}
            color="error"
            startIcon={<Icon>save</Icon>}
            style={{ marginRight: "20px" }}
            disabled={
              loading ||
              (declaration?.gmrState !== "ERROR" &&
                declaration?.gmrState !== "DRAFT" &&
                declaration?.gmrState !== "NOT_FINALISABLE")
            }
          >
            Reject
          </Button>
          <Button
            onClick={handleClose}
            color="error"
            startIcon={<Icon>close</Icon>}
            disabled={loading}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDuplicateDialog}
        onClose={handleCloseDuplicateDialog}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2.5,
            minWidth: 420,
            boxShadow: 8,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            pb: 1,
          }}
        >
          <Icon color="info" sx={{ fontSize: 28 }}>
            file_copy
          </Icon>
          <Typography variant="h6" fontWeight={600}>
            Duplicate declaration
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to duplicate the declaration ?
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "space-between",
            pt: 3,
          }}
        >
          <Button
            onClick={handleCloseDuplicateDialog}
            variant="outlined"
            color="inherit"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>

          <Button
            color="info"
            variant="contained"
            onClick={duplicateDeclaration}
            sx={{
              borderRadius: 2,
              minWidth: 120,
              fontWeight: 600,
            }}
          >
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={popup.open}
        autoHideDuration={3500}
        onClose={() => setPopup((p) => ({ ...p, open: false }))}
        // position right bottom
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={popup.severity}
          onClose={() => setPopup((p) => ({ ...p, open: false }))}
          variant="filled"
        >
          {popup.message}
        </Alert>
      </Snackbar>
    </>
  );
}
